import { useQuery } from '@tanstack/react-query'

import { getApiBaseUrl } from '@/config/apiConfig'
import { useNativeOnlineStatus } from '@/lib/hooks/useNativeOnlineStatus'

const THIRTY_SECONDS = 30000
const FETCH_TIMEOUT_MS = 8000

export const maintenanceStatusQueryKey = ['maintenanceStatus'] as const

/** Shape returned by `GET /maintenance-status`. */
export interface MaintenanceStatusResponse {
  enabled: boolean
  message: string | null
  startedAt: string | null
  eta: string | null
}

/** Derived state consumed by the overlay/banner. */
export interface MaintenanceState {
  isMaintenance: boolean
  message: string | null
  /** epoch ms parsed from `eta`, or null when absent/unparseable */
  estimatedEndAt: number | null
}

/**
 * Bare `fetch` (NOT the axios `api` instance): must be side-effect-free,
 * pre-login-safe, and immune to the api-client 503 interceptor (no recursion).
 * Times out so a hung request never stalls the poll.
 */
export async function fetchMaintenanceStatus(
  rqSignal?: AbortSignal,
): Promise<MaintenanceStatusResponse> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  const onAbort = (): void => controller.abort()
  rqSignal?.addEventListener('abort', onAbort)

  try {
    // getApiBaseUrl() is '' on web (→ relative '/maintenance-status') and an
    // absolute origin on native (capacitor:// can't use relative URLs).
    const res = await fetch(`${getApiBaseUrl()}/maintenance-status`, {
      credentials: 'include',
      cache: 'no-store',
      // Forward-To-Api is REQUIRED on staging/prod: Caddyfile_prod routes a
      // request to PHP only when it carries this header — without it this poll
      // is routed to the PWA and gets the SPA shell back, so maintenance is
      // never detected and the in-app overlay never fires. (Local dev routes by
      // Accept, so it works there regardless — which is why this slipped through.)
      headers: { Accept: 'application/json', 'Forward-To-Api': 'true' },
      signal: controller.signal,
    })
    if (!res.ok) {
      throw new Error(`maintenance-status responded ${res.status}`)
    }
    return (await res.json()) as MaintenanceStatusResponse
  } finally {
    clearTimeout(timer)
    rqSignal?.removeEventListener('abort', onAbort)
  }
}

/**
 * Polls `/maintenance-status`. Returns derived state where `isMaintenance`
 * defaults to FALSE on undefined/error/offline — this is the offline fallback:
 * an offline device never shows the maintenance screen (it can't reach the new
 * backend anyway and keeps using cached data + the queue).
 */
export function useMaintenanceStatus(): MaintenanceState {
  const isOnline = useNativeOnlineStatus()

  const { data } = useQuery({
    queryKey: maintenanceStatusQueryKey,
    queryFn: ({ signal }) => fetchMaintenanceStatus(signal),
    refetchInterval: THIRTY_SECONDS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: 'always',
    retry: false,
    staleTime: 0,
    networkMode: 'always',
    // Don't poll while offline; resume on reconnect (refetchOnReconnect).
    enabled: isOnline,
    throwOnError: false,
  })

  const etaMs = data?.eta ? Date.parse(data.eta) : Number.NaN

  return {
    // Gate on isOnline too, so a stale "enabled:true" can't keep the overlay up
    // after the device drops offline.
    isMaintenance: isOnline && data?.enabled === true,
    message: data?.message ?? null,
    estimatedEndAt: Number.isFinite(etaMs) ? etaMs : null,
  }
}
